import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../lib/supabase';

const ArticleContext = createContext();

export const useArticleContext = () => {
  const context = useContext(ArticleContext);
  if (!context) {
    throw new Error('useArticleContext must be used within an ArticleProvider');
  }
  return context;
};

export const ArticleProvider = ({ children }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load articles from Supabase on mount
  useEffect(() => {
    fetchArticles();
    // Set up real-time subscription
    const subscription = supabase
      .channel('automatic_article_addition_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'automatic_article_addition_a7b9c2d1e3' },
        (payload) => {
          console.log('Real-time change received:', payload);
          handleRealtimeChange(payload);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('automatic_article_addition_a7b9c2d1e3')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching articles:', error);
        setError(error.message);
        return;
      }
      setArticles(data || []);
    } catch (err) {
      console.error('Error in fetchArticles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRealtimeChange = (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    switch (eventType) {
      case 'INSERT':
        setArticles(prev => [newRecord, ...prev]);
        break;
      case 'UPDATE':
        setArticles(prev => prev.map(article => article.id === newRecord.id ? newRecord : article));
        break;
      case 'DELETE':
        setArticles(prev => prev.filter(article => article.id !== oldRecord.id));
        break;
      default:
        break;
    }
  };

  // Initialize deleted articles analytics table
  const initializeDeletedArticlesTable = async () => {
    try {
      const { error } = await supabase.rpc('exec_sql', {
        sql: `
          -- Create deleted articles analytics table if it doesn't exist
          CREATE TABLE IF NOT EXISTS deleted_articles_analytics_d8f2a1b3c9 (
            id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
            original_article_id INTEGER NOT NULL,
            title TEXT,
            content_length INTEGER,
            word_count INTEGER,
            owner TEXT,
            associated_user TEXT,
            title_tag TEXT,
            meta_description TEXT,
            created_at TIMESTAMP,
            updated_at TIMESTAMP,
            deleted_at TIMESTAMP DEFAULT NOW(),
            deleted_by TEXT DEFAULT 'system',
            content_preview TEXT
          );
          -- Enable RLS if not already enabled
          ALTER TABLE deleted_articles_analytics_d8f2a1b3c9 ENABLE ROW LEVEL SECURITY;
          -- Create policies if they don't exist
          DO $$
          BEGIN
            IF NOT EXISTS ( SELECT 1 FROM pg_policies WHERE tablename='deleted_articles_analytics_d8f2a1b3c9' AND policyname='Enable read access for all users') THEN
              CREATE POLICY "Enable read access for all users" ON deleted_articles_analytics_d8f2a1b3c9 FOR SELECT USING (true);
            END IF;
            IF NOT EXISTS ( SELECT 1 FROM pg_policies WHERE tablename='deleted_articles_analytics_d8f2a1b3c9' AND policyname='Enable insert access for all users') THEN
              CREATE POLICY "Enable insert access for all users" ON deleted_articles_analytics_d8f2a1b3c9 FOR INSERT WITH CHECK (true);
            END IF;
          END $$;
          -- Create indexes if they don't exist
          CREATE INDEX IF NOT EXISTS idx_deleted_articles_original_id ON deleted_articles_analytics_d8f2a1b3c9 (original_article_id);
          CREATE INDEX IF NOT EXISTS idx_deleted_articles_owner ON deleted_articles_analytics_d8f2a1b3c9 (owner);
          CREATE INDEX IF NOT EXISTS idx_deleted_articles_user ON deleted_articles_analytics_d8f2a1b3c9 (associated_user);
          CREATE INDEX IF NOT EXISTS idx_deleted_articles_deleted_at ON deleted_articles_analytics_d8f2a1b3c9 (deleted_at);
        `
      });
      if (error) {
        console.warn('Could not initialize deleted articles table via RPC, attempting direct creation:', error);
        await createDeletedArticlesTableDirect();
      }
    } catch (err) {
      console.warn('RPC method not available, using direct table creation:', err);
      await createDeletedArticlesTableDirect();
    }
  };

  // Fallback method to create table directly
  const createDeletedArticlesTableDirect = async () => {
    try {
      // Check if table exists first
      const { data: tableExists } = await supabase
        .from('deleted_articles_analytics_d8f2a1b3c9')
        .select('id')
        .limit(1);
      console.log('Deleted articles analytics table already exists');
    } catch (error) {
      if (error.message.includes('relation') && error.message.includes('does not exist')) {
        console.log('Deleted articles analytics table needs to be created manually');
        // Table doesn't exist, but we can't create it directly via client
        // We'll handle this gracefully by skipping analytics if table doesn't exist
      }
    }
  };

  const saveArticle = async (articleData) => {
    try {
      const now = new Date().toISOString();
      if (articleData.id) {
        // Update existing article, ensuring all fields including owner and associated_user are updated
        const { data, error } = await supabase
          .from('automatic_article_addition_a7b9c2d1e3')
          .update({
            title: articleData.title,
            content: articleData.content,
            title_tag: articleData.title_tag,
            meta_description: articleData.meta_description,
            owner: articleData.owner || 'Default Client', // Ensure owner is set
            associated_user: articleData.associated_user || null,
            updated_at: now
          })
          .eq('id', articleData.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating article:', error);
          throw error;
        }
        return data;
      } else {
        // Create new article, ensuring all fields including owner and associated_user are included
        const { data, error } = await supabase
          .from('automatic_article_addition_a7b9c2d1e3')
          .insert({
            title: articleData.title || 'Untitled Article',
            content: articleData.content || '',
            title_tag: articleData.title_tag || '',
            meta_description: articleData.meta_description || '',
            owner: articleData.owner || 'Default Client', // Ensure owner is set
            associated_user: articleData.associated_user || null,
            created_at: now,
            updated_at: now
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating article:', error);
          throw error;
        }
        return data;
      }
    } catch (err) {
      console.error('Error in saveArticle:', err);
      throw err;
    }
  };

  const getArticle = (id) => {
    return articles.find(article => article.id === parseInt(id));
  };

  // Helper function to calculate word count
  const calculateWordCount = (content) => {
    if (!content) return 0;
    return content.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length;
  };

  // Helper function to create content preview
  const createContentPreview = (content, maxLength = 500) => {
    if (!content) return '';
    const textContent = content.replace(/<[^>]*>/g, '');
    return textContent.length > maxLength ? textContent.substring(0, maxLength) + '...' : textContent;
  };

  // Store article analytics before deletion
  const storeArticleAnalytics = async (article) => {
    try {
      const wordCount = calculateWordCount(article.content);
      const contentLength = article.content ? article.content.length : 0;
      const contentPreview = createContentPreview(article.content);

      const analyticsData = {
        original_article_id: article.id,
        title: article.title || 'Untitled Article',
        content_length: contentLength,
        word_count: wordCount,
        owner: article.owner || 'Unknown',
        associated_user: article.associated_user || null,
        title_tag: article.title_tag || '',
        meta_description: article.meta_description || '',
        created_at: article.created_at,
        updated_at: article.updated_at,
        deleted_at: new Date().toISOString(),
        deleted_by: 'user', // Could be enhanced to track actual user
        content_preview: contentPreview
      };

      const { data, error } = await supabase
        .from('deleted_articles_analytics_d8f2a1b3c9')
        .insert(analyticsData)
        .select()
        .single();

      if (error) {
        console.error('Error storing article analytics:', error);
        // Don't throw error here - we still want to proceed with deletion
        return null;
      }
      console.log('Article analytics stored successfully:', data);
      return data;
    } catch (err) {
      console.error('Error in storeArticleAnalytics:', err);
      // Don't throw error here - we still want to proceed with deletion
      return null;
    }
  };

  const deleteArticle = async (id) => {
    try {
      // First, get the article data for analytics
      const articleToDelete = getArticle(id);
      if (!articleToDelete) {
        throw new Error('Article not found');
      }

      // Store analytics before deletion
      console.log('Storing article analytics before deletion...');
      await storeArticleAnalytics(articleToDelete);

      // Now delete the article from the main table
      const { error } = await supabase
        .from('automatic_article_addition_a7b9c2d1e3')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting article:', error);
        throw error;
      }

      // Update local state
      setArticles(prev => prev.filter(article => article.id !== id));
      console.log(`Article ${id} deleted successfully and analytics stored`);
      return true;
    } catch (err) {
      console.error('Error in deleteArticle:', err);
      throw err;
    }
  };

  const importFromWebhook = async (webhookData) => {
    try {
      const article = {
        title: webhookData.title || 'Imported Article',
        content: webhookData.content || webhookData.html || '',
        title_tag: webhookData.title_tag || webhookData.metaTitle || webhookData.meta_title || '',
        meta_description: webhookData.meta_description || webhookData.metaDescription || '',
        owner: webhookData.owner || webhookData.client || 'Imported Client',
        associated_user: webhookData.associated_user || webhookData.user || webhookData.author || null
      };
      return await saveArticle(article);
    } catch (err) {
      console.error('Error importing from webhook:', err);
      throw err;
    }
  };

  const exportArticle = (id) => {
    const article = getArticle(id);
    if (!article) return null;
    return {
      id: article.id,
      title: article.title,
      content: article.content,
      title_tag: article.title_tag,
      meta_description: article.meta_description,
      owner: article.owner,
      associated_user: article.associated_user,
      html: article.content, // Clean HTML output
      wordCount: calculateWordCount(article.content),
      created_at: article.created_at,
      updated_at: article.updated_at
    };
  };

  const getArticlesByOwner = (owner) => {
    return articles.filter(article => article.owner === owner);
  };

  const getArticlesByUser = (user) => {
    return articles.filter(article => article.associated_user === user);
  };

  const getUniqueOwners = () => {
    return [...new Set(articles.map(article => article.owner).filter(Boolean))].sort();
  };

  const getUniqueUsers = () => {
    return [...new Set(articles.map(article => article.associated_user).filter(Boolean))].sort();
  };

  // Get deleted articles analytics (for potential future use)
  const getDeletedArticlesAnalytics = async (limit = 50) => {
    try {
      const { data, error } = await supabase
        .from('deleted_articles_analytics_d8f2a1b3c9')
        .select('*')
        .order('deleted_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching deleted articles analytics:', error);
        return [];
      }
      return data || [];
    } catch (err) {
      console.error('Error in getDeletedArticlesAnalytics:', err);
      return [];
    }
  };

  // Initialize the deleted articles table on component mount
  useEffect(() => {
    initializeDeletedArticlesTable();
  }, []);

  const value = {
    articles,
    loading,
    error,
    saveArticle,
    getArticle,
    deleteArticle,
    importFromWebhook,
    exportArticle,
    getArticlesByOwner,
    getArticlesByUser,
    getUniqueOwners,
    getUniqueUsers,
    getDeletedArticlesAnalytics,
    // Available for future analytics features
    refetch: fetchArticles
  };

  return (
    <ArticleContext.Provider value={value}>
      {children}
    </ArticleContext.Provider>
  );
};